import React, { useEffect, useMemo, useReducer, useRef, useState, useCallback } from 'react';
import { Button, Dialog, DialogContent, Stack } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import Api from './API';
import { styles } from './styles';
import AddToCandidatePool from '../CandidateSourcing/AddToCandidatePool';
import { PlaylistAdd, Visibility } from '@mui/icons-material';
import { InitialCandidatesState, CandidatesReducer } from './reducer';
import { CandidatesState, ICableData, TCandidate } from './types';
import GenericTable from '../Components/GenericTable/GenericTable';
import CandidatesTableRows from './CandidatesTableRows';
import { headerStyle } from '../Candidate/config';
import { candidatesTableColumn } from './config';
import TotalSelectedIndicatorWithActions from '../Components/GenericTable/TotalSelectedIndicatorWithActions';
import ConditionsDialog from './ConditionsDialog';
import GenericDialog from '../Components/Modals/GenericDialog';
import { tableSearchButtonStyles, toggleScreenButtonStyles } from '../shared/commonStyles';
import CandidateExpandedContent from './CandidateExpandedContent';
import { useWebSockets } from '../Components/Utilities/useWebSockets';
import { useCandidatePoolModal } from '../CandidateSourcing/hooks/useCandidatePoolModal';
import WithPoolModal from '../CandidateSourcing/WithPoolModal';
import { Condition } from '../CandidateSourcing/types';
import CandidateGroup from '../CandidateGroup/CandidateGroup';
import { ICandidateGroup } from '../AgenticMatching/types';
interface ApiResponse {
  resHead: {
    [key: string]: string;
  };
  res: {
    conditions: Condition[];
    candidates: TCandidate[];
  };
}

export default function CandidateSourcingScreening({
  searchId,
  conditions,
  googleApiKey
}: {
  searchId: number;
  conditions: Condition[];
  googleApiKey?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentConditions, setCurrentConditions] = useState<Condition[]>(conditions);
  const [isAddingToPools, setIsAddingToPools] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [CandidatesState, dispatch] = useReducer(CandidatesReducer, InitialCandidatesState);
  const [isConditionsDialogOpen, setIsConditionsDialogOpen] = useState(false);
  const [candidates, setCandidates] = useState<TCandidate[]>([]);
  const [expandedPools, setExpandedPools] = useState<Set<number>>(new Set());
  const [isCandidateGroupModalOpen, setIsCandidateGroupModalOpen] = useState(false);
  const [selectedCandidateGroup, setSelectedCandidateGroup] = useState<ICandidateGroup | null>(
    null
  );
  const [isRevealContactsDialogOpen, setIsRevealContactsDialogOpen] = useState(false);
  const { candidatesTableState, candidatesTableSettings } = CandidatesState;
  const { selectedItemIds } = candidatesTableState;
  const expandableCellIds = [
    ...currentConditions.map((condition) => `condition_${condition.id}`),
    'matching_percent'
  ];
  const candidatePoolModalHook = useCandidatePoolModal();
  const { handlePoolClick } = candidatePoolModalHook;
  const { subscribeToChannel } = useWebSockets('CandidateSourcingChannel', (data: ICableData) => {
    if (data.kind === 'contacts_revealed') {
      refetchCandidates();
    }
  });

  const { mutate: refineScreening } = useMutation({
    mutationFn: async () => {
      const res = (await Api.refineScreening(searchId, currentConditions)) as ApiResponse;
      if (res && res.res) {
        setCurrentConditions(res.res.conditions);
      }
    },
    onSuccess: () => {
      refetchCandidates();
    }
  });

  const { refetch: refetchCandidates, isFetching: isFetchingCandidates } = useQuery({
    queryKey: [
      'search_candidates',
      searchId,
      candidatesTableSettings.page,
      candidatesTableSettings.limit
    ],
    queryFn: async () => {
      const res = (await Api.getScreeningCandidates(
        searchId,
        candidatesTableSettings.page,
        candidatesTableSettings.limit
      )) as ApiResponse;
      setTotalItems(parseInt(res.resHead['x-total-count']));
      setTotalPages(parseInt(res.resHead['x-total-pages']));
      setCandidates(res.res.candidates);
      return res.res.candidates;
    },
    initialData: [],
    refetchInterval: (data) => {
      if (!data) return 10000;
      const allAssessed = data.every(
        (candidate) =>
          candidate.matching_percent !== undefined && candidate.matching_percent !== null
      );
      return allAssessed ? false : 10000;
    },
    refetchIntervalInBackground: true
  });

  const addCandidatesToPools = () => {
    setIsAddingToPools(true);
  };

  const getDynamicColumns = () => {
    const baseColumns = [...candidatesTableColumn];
    const conditionColumns = currentConditions.map((condition, index) => ({
      id: `condition_${condition.id || condition.content}`,
      label: condition.short_label || `Condition ${index + 1}`,
      enabled: true,
      sortable: false,
      canToggle: false,
      tooltip: condition.content
    }));
    return [...baseColumns, ...conditionColumns];
  };

  const handleRevealContacts = useCallback(
    async (candidate: TCandidate) => {
      setCandidates(
        candidates.map((c) => (c.id === candidate.id ? { ...c, revealing_contacts: true } : c))
      );
      await Api.revealContacts(searchId, [candidate.candidate_group.id]);
    },
    [candidates, searchId]
  );

  const handleBulkRevealContacts = useCallback(() => {
    setIsRevealContactsDialogOpen(true);
  }, []);

  const handleConfirmRevealContacts = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async (_url: string, _id: number) => {
      const selectedCandidates = candidates.filter((candidate) =>
        selectedItemIds.includes(candidate.id)
      );
      const candidateGroupIds = selectedCandidates.map((candidate) => candidate.candidate_group.id);
      const uniqueGroupIds = Array.from(new Set(candidateGroupIds));

      setCandidates(
        candidates.map((c) =>
          selectedItemIds.includes(c.id) ? { ...c, revealing_contacts: true } : c
        )
      );
      setIsRevealContactsDialogOpen(false);
      await Api.revealContacts(searchId, uniqueGroupIds);
    },
    [candidates, selectedItemIds, searchId]
  );

  const tokenCost = useMemo(() => {
    const selectedCandidates = candidates.filter((candidate) =>
      selectedItemIds.includes(candidate.id)
    );
    const uniqueGroups = new Map<number, ICandidateGroup>();
    selectedCandidates.forEach((candidate) => {
      if (!uniqueGroups.has(candidate.candidate_group.id)) {
        uniqueGroups.set(candidate.candidate_group.id, candidate.candidate_group);
      }
    });
    return Array.from(uniqueGroups.values()).filter(
      (group) => !group.contacts_revealed && group.emails.length === 0 && group.phones.length === 0
    ).length;
  }, [candidates, selectedItemIds]);

  const handleSelectAll = useCallback(async () => {
    const response = await Api.allCandidateGroupIds(searchId);
    dispatch({ type: 'SET_SELECTED_ITEM_IDS', payload: (response.res as { ids: number[] }).ids });
  }, [searchId, dispatch]);

  const handleTogglePoolsExpansion = useCallback((candidateGroupId: number) => {
    setExpandedPools((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(candidateGroupId)) {
        newSet.delete(candidateGroupId);
      } else {
        newSet.add(candidateGroupId);
      }
      return newSet;
    });
  }, []);

  const allItemsInPageSelected = useMemo(() => {
    return candidates.every((candidate) => selectedItemIds.includes(candidate.id));
  }, [selectedItemIds, candidates]);

  const handleOpenCandidateGroup = useCallback((group: ICandidateGroup) => {
    setSelectedCandidateGroup(group);
    setIsCandidateGroupModalOpen(true);
  }, []);

  const handleCloseCandidateGroup = useCallback(() => {
    setIsCandidateGroupModalOpen(false);
    setSelectedCandidateGroup(null);
  }, []);

  const candidateGroups = useMemo(() => {
    const groupsMap = new Map<number, ICandidateGroup>();
    const groups: ICandidateGroup[] = [];
    candidates.forEach((candidate) => {
      if (!groupsMap.has(candidate.candidate_group.id)) {
        groupsMap.set(candidate.candidate_group.id, candidate.candidate_group);
        groups.push(candidate.candidate_group);
      }
    });
    return groups;
  }, [candidates]);

  const currentGroupIndex = useMemo(() => {
    if (!selectedCandidateGroup) return -1;
    return candidateGroups.findIndex((group) => group.id === selectedCandidateGroup.id);
  }, [selectedCandidateGroup, candidateGroups]);

  const handleNavigateCandidateGroup = useCallback(
    (index: number) => {
      if (index >= 0 && index < candidateGroups.length) {
        setSelectedCandidateGroup(candidateGroups[index]);
      }
    },
    [candidateGroups]
  );

  const MemoizedTableRows = useCallback(
    (props: {
      column:
        | CandidatesState['candidatesTableState']['columns'][number]
        | {
            id: string;
            label: string;
            enabled: boolean;
            sortable: boolean;
            canToggle: boolean;
            tooltip?: string;
          };
      item: TCandidate;
      tableHovered: boolean;
    }) => (
      <CandidatesTableRows
        {...props}
        onRevealContacts={handleRevealContacts}
        onViewPool={handlePoolClick}
        expandedPools={expandedPools}
        onTogglePoolsExpansion={handleTogglePoolsExpansion}
        onOpenCandidateGroup={handleOpenCandidateGroup}
      />
    ),
    [
      handleRevealContacts,
      handlePoolClick,
      expandedPools,
      handleTogglePoolsExpansion,
      handleOpenCandidateGroup
    ]
  );

  useEffect(() => {
    subscribeToChannel();
  }, [subscribeToChannel]);

  useEffect(() => {
    if (!selectedCandidateGroup) {
      return;
    }
    const updatedCandidate = candidates.find(
      (candidate) => candidate.candidate_group.id === selectedCandidateGroup.id
    );
    if (updatedCandidate && updatedCandidate.candidate_group !== selectedCandidateGroup) {
      setSelectedCandidateGroup(updatedCandidate.candidate_group);
    }
  }, [candidates, selectedCandidateGroup]);

  return (
    <Stack sx={styles.candidatesPageContainer} ref={containerRef}>
      <WithPoolModal candidatePoolModalHook={candidatePoolModalHook} refetch={refetchCandidates}>
        <Stack sx={styles.candidatesPageHeader}>Screening</Stack>
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            onClick={() => setIsConditionsDialogOpen(true)}
            sx={tableSearchButtonStyles}
          >
            Edit Conditions
          </Button>
          <Button
            variant="contained"
            onClick={() =>
              (window.location.href = `/admin/candidate_sourcing/searches/${searchId}`)
            }
            sx={toggleScreenButtonStyles}
          >
            Back to search
          </Button>
        </Stack>
        <GenericTable
          label="candidates list"
          items={candidates}
          isLoadingItems={isFetchingCandidates}
          tableState={{
            ...candidatesTableState,
            columns: getDynamicColumns()
          }}
          dispatch={dispatch}
          headerStyle={headerStyle}
          paginationState={candidatesTableSettings}
          pagination={{ totalItems, totalPages }}
          TableRows={MemoizedTableRows}
          TableActionList={undefined}
          allowMultipleSelection={candidates.length ? true : false}
          titleCellId="id"
          userPreferenceSource="candidates"
          ExpandedContent={CandidateExpandedContent}
          expandableCellIds={expandableCellIds}
        />
        <TotalSelectedIndicatorWithActions
          totalSelected={selectedItemIds.length}
          parentDiv={containerRef.current}
          totalItems={totalItems}
          clearAllEnabled={selectedItemIds.length > 0}
          onSelectAll={handleSelectAll}
          canSelectAll={allItemsInPageSelected && selectedItemIds.length < totalItems}
          dispatch={dispatch}
          actionButtonId="delete-candidate-action-button"
          actionButtonsList={[
            {
              id: 'add-to-pool',
              label: 'Add to Pool',
              icon: <PlaylistAdd />,
              styles: {
                color: '#084D6D',
                background: '#DEF3F2'
              },
              callback: () => {
                addCandidatesToPools();
              }
            },
            {
              id: 'reveal-contacts',
              label: 'Reveal Contacts',
              icon: <Visibility />,
              styles: {
                color: '#084D6D',
                background: '#DEF3F2'
              },
              callback: () => {
                handleBulkRevealContacts();
              }
            }
          ]}
        />
        <AddToCandidatePool
          isOpen={isAddingToPools}
          handleClose={() => setIsAddingToPools(false)}
          selectedCandidateGroups={candidates
            .filter((candidate) => selectedItemIds.includes(candidate.id))
            .map((candidate) => candidate.candidate_group)}
          onRemoveCandidateGroup={(candidateGroupId) => {
            dispatch({ type: 'REMOVE_SELECTED_ITEM', payload: candidateGroupId });
          }}
          refetch={refetchCandidates}
        />
        <ConditionsDialog
          isOpen={isConditionsDialogOpen}
          handleClose={() => setIsConditionsDialogOpen(false)}
          conditions={currentConditions}
          onConditionsChange={setCurrentConditions}
          onSubmit={() => refineScreening()}
          onResetConditions={() => setCurrentConditions(conditions)}
          submitButtonText="Refine Screening"
        />
      </WithPoolModal>
      <Dialog
        open={isCandidateGroupModalOpen && !!selectedCandidateGroup}
        onClose={handleCloseCandidateGroup}
        fullWidth
        maxWidth="lg"
        scroll="body"
      >
        {selectedCandidateGroup && (
          <>
            <DialogContent dividers sx={{ p: 0, overflowY: 'hidden' }}>
              <CandidateGroup
                group={selectedCandidateGroup}
                refetchCandidates={refetchCandidates}
                allGroups={candidateGroups}
                currentIndex={currentGroupIndex}
                onNavigate={handleNavigateCandidateGroup}
              />
            </DialogContent>
          </>
        )}
      </Dialog>
      <GenericDialog
        isDialogOpen={isRevealContactsDialogOpen}
        setDialogOpen={setIsRevealContactsDialogOpen}
        title="Reveal Contacts"
        description={`This will cost you ${tokenCost} token${tokenCost !== 1 ? 's' : ''}. Do you want to continue?`}
        buttonCallback={handleConfirmRevealContacts}
        callbackLoading={false}
        buttonText="Continue"
        url=""
        alternateColor={true}
      />
    </Stack>
  );
}
